<template>
  <BTable
    v-bind="propsWithoutItems"
    :items="itemsFiltered"
    :first-column-size="firstColumnSize"
    ref="tableRef"
    :is-loading="isLoading"
    :total-count="totalCount"
    :client-side-filtering="true"
    :enable-client-side-sorting="enableClientSideSorting"
    :client-side-pagination="true"
    :enable-views="enableViews"
    :is-editable="isEditable"
    :no-padding="noPadding"
    :cast-shadow="castShadow"
    :sort-ascending="sortAscending"
    :should-draw-columns="shouldDrawColumns"
    :force-fixed-width="forceFixedWidth"
    :enable-only-search-section="enableOnlySearchSection"
    :page-size-options-available="pageSizeOptionsAvailable"
    :buttonActionOptions="buttonActionOptions"
    @on-button-action-options-changed="(id) => emits('onButtonActionOptionsChanged', id)"
    @download="download"
    @page-index-changed="pageIndexChange"
    @page-size-changed="pageSizeChange"
    @client-side-filters-changed="clientFiltersChanged"
    @bulk-creation-changes-saved="() => emits('bulkCreationChangesSaved')">
    <template
      v-for="(_, key) in $slots"
      #[key]="{ item, value }">
      <slot
        :name="key"
        v-bind="{ item: item as T, value }" />
    </template>
  </BTable>
</template>

<script setup lang="ts" generic="T extends TableEntity">
  import { DateTime } from 'luxon';
  import { computed, ref, useTemplateRef } from 'vue';

  import { TableEntity } from '@/common/components/controls/table/TableEntity';
  import TableProps from '@/common/components/controls/table/TableProps';
  import { objectOmitted } from '@/globals';
  import DownloadUtils from '@/utils/DownloadUtils';

  import BTable from './Table.vue';

  type Filter = { and?: (obj: T) => boolean; or?: (obj: T) => boolean };

  type Props = Omit<TableProps<T>, 'clientSideFiltering' | 'clientSidePagination'> & {
    handleDownload?: boolean;
  };
  interface Emits {
    (e: 'download', fileType: string, items: T[]);
    (e: 'inlineEditingDone', payload: unknown);
    (e: 'bulkCreationChangesSaved');
    (e: 'onButtonActionOptionsChanged', id: string);
  }
  const props = withDefaults(defineProps<Props>(), {
    allowSelectionAll: true,
    resetSelectionOnUpdate: true,
    sortAscending: true,
    isLoading: false,
    enableSearchSection: true,
    castShadow: true,
    enableColumnReorder: true,
    showTable: true,
    showHeaders: true,
    showSaveBtn: true,
    useFiltersDropDown: true,
    enableViews: false,
    enableClientSideSorting: true,
    buttonActionOptions: null,
    assignInlineEditButtonStyles: true,
  });
  const emits = defineEmits<Emits>();
  const tableRef = useTemplateRef('tableRef');

  const tableFilters = ref<{ filters: Filter; sort: string }>({ filters: {}, sort: '' });
  const pageIndex = ref(1);
  const pageSize = ref(10);

  const propsWithoutItems = computed(() => {
    return objectOmitted(props, ['items']);
  });
  const itemsFiltered = computed(() => {
    const { filters } = tableFilters.value;

    let items = props.items;
    if (filters.or) items = items.filter(filters.or);
    if (filters.and) items = items.filter(filters.and);

    return items;
  });
  const totalCount = computed(() => {
    return props.totalCount >= 0 ? props.totalCount : itemsFiltered.value.length;
  });
  const selectedItems = computed(() => tableRef.value?.selectedItems);

  function clientFiltersChanged({ functions, sort }: { functions: Filter; sort: string }) {
    tableFilters.value = { filters: functions, sort };
  }
  function pageSizeChange(size: number) {
    pageSize.value = size;
  }
  function pageIndexChange(index: number) {
    pageIndex.value = index;
  }
  function download(fileType: 'xlsx' | 'csv') {
    if (props.handleDownload) {
      const title = `${props.category} - ${DateTime.local()}`.replace(/[*?:\\\/[\]]/g, '-');
      const rows = itemsFiltered.value.map((item) => {
        return Object.entries(props.columns).map(([k, v]) => {
          return item[k] ?? item[v.property] ?? item[v.sourceFilterProperty] ?? '';
        });
      });
      const columnValues = Object.values(props.columns);
      const types = columnValues.map((c) => c.type);
      const names = columnValues.map((c) => c.label);
      if (fileType === 'csv') {
        DownloadUtils.downloadAsCsv(title, rows, types, names);
      } else {
        DownloadUtils.downloadAsXlsx(title, rows, types, names);
      }
    }
    emits('download', fileType, itemsFiltered.value);
  }

  defineExpose({ selectedItems });
</script>
